import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, Loader2Icon, MailIcon, ShieldCheckIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
type Step = 'email' | 'otp';
interface SignInPageProps {
  onSignIn: (email: string) => void;
}
const CREDENTIALS: Record<string, string> = {
  'ajman@ta.gov.ae': '123123',
  'sharjah@police.ae': '123123',
  'bahrain@pair2.ae': '123123',
  'bahrain2@pair2.ae': '987123',
  'dubai@police.ae': '123123'
};
export function SignInPage({
  onSignIn
}: SignInPageProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('يرجى إدخال بريد إلكتروني صحيح');
      return;
    }
    if (!(email.toLowerCase().trim() in CREDENTIALS)) {
      setError('هذا البريد الإلكتروني غير مصرح له بالوصول');
      return;
    }
    setError('');
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('otp');
    }, 800);
  };
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) {
      setError('يرجى إدخال رمز التحقق كاملاً');
      return;
    }
    const expectedOtp = CREDENTIALS[email.toLowerCase().trim()];
    if (otp !== expectedOtp) {
      setError('رمز التحقق غير صحيح');
      return;
    }
    setError('');
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onSignIn(email.toLowerCase().trim());
    }, 1500);
  };
  return <div className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden" style={{
    background: '#EDF1F4'
  }} dir="rtl" data-id="element-840">
      {/* ===== BACKGROUND DECORATION ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-841">
        <div className="absolute -top-40 -right-40 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.08) 0%, transparent 70%)'
      }} data-id="element-842" />
        <div className="absolute -bottom-40 -left-40 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.05) 0%, transparent 70%)'
      }} data-id="element-843" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-844" />
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-10 w-full max-w-md px-6 flex flex-col items-center" data-id="element-845">
        {/* Logo */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="mb-10" data-id="element-846">
          <img src={PAIR2_LOGO} alt="Pair²" className="h-12 w-auto" style={{
          filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
        }} draggable={false} data-id="element-847" />
        </motion.div>

        {/* Card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} className="w-full bg-white/80 backdrop-blur-xl rounded-3xl p-8 border border-white/40 shadow-[0_20px_40px_-15px_rgba(14,93,85,0.1)]" data-id="element-848">
          <AnimatePresence mode="wait" data-id="element-849">
            {step === 'email' ? <motion.div key="email-step" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-850">
                <div className="text-center mb-8" data-id="element-851">
                  <h1 className="font-arabic-display font-bold text-2xl text-[#0B1328] mb-2" data-id="element-852">
                    تسجيل الدخول
                  </h1>
                  <p className="font-arabic-body text-[#6E7A8F] text-sm" data-id="element-853">
                    أدخل بريدك الإلكتروني للوصول إلى العرض التقديمي
                  </p>
                </div>

                <form onSubmit={handleEmailSubmit} className="flex flex-col gap-5" data-id="element-854">
                  <div data-id="element-855">
                    <label className="block font-arabic-body text-sm font-semibold text-[#1A1F2B] mb-2" data-id="element-856">
                      البريد الإلكتروني
                    </label>
                    <div className="relative" data-id="element-857">
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none" data-id="element-858">
                        <MailIcon className="w-5 h-5 text-[#B0BAC8]" data-id="element-859" />
                      </div>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className="w-full bg-[#f8f9fb] border border-[#E2E8F0] rounded-xl py-3.5 pr-12 pl-4 text-left font-sans text-[#0B1328] placeholder:text-[#B0BAC8] focus:outline-none focus:ring-2 focus:ring-[#2FA99C]/50 focus:border-[#2FA99C] transition-all" dir="ltr" required data-id="element-860" />
                    </div>
                    {error && <p className="mt-2 text-sm text-red-500 font-arabic-body" data-id="element-861">
                        {error}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading || !email} className="mt-2 w-full bg-[#2FA99C] hover:bg-[#268f84] text-white rounded-xl py-3.5 font-arabic-body font-bold text-lg flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_8px_20px_-6px_rgba(47,169,156,0.4)] hover:shadow-[0_12px_24px_-6px_rgba(47,169,156,0.5)] hover:-translate-y-0.5" data-id="element-862">
                    {isLoading ? <Loader2Icon className="w-5 h-5 animate-spin" data-id="element-863" /> : <>
                        <span data-id="element-864">المتابعة</span>
                        <ArrowRightIcon className="w-5 h-5 rotate-180" data-id="element-865" />
                      </>}
                  </button>
                </form>
              </motion.div> : <motion.div key="otp-step" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-866">
                <div className="text-center mb-8" data-id="element-867">
                  <div className="mx-auto w-12 h-12 bg-[#2FA99C]/10 rounded-full flex items-center justify-center mb-4" data-id="element-868">
                    <ShieldCheckIcon className="w-6 h-6 text-[#2FA99C]" data-id="element-869" />
                  </div>
                  <h1 className="font-arabic-display font-bold text-2xl text-[#0B1328] mb-2" data-id="element-870">
                    التحقق من البريد
                  </h1>
                  <p className="font-arabic-body text-[#6E7A8F] text-sm leading-relaxed" data-id="element-871">
                    تم إرسال رمز التحقق إلى
                    <br data-id="element-872" />
                    <span className="font-sans font-medium text-[#0B1328]" dir="ltr" data-id="element-873">
                      {email}
                    </span>
                  </p>
                </div>

                <form onSubmit={handleOtpSubmit} className="flex flex-col gap-5" data-id="element-874">
                  <div data-id="element-875">
                    <label className="block font-arabic-body text-sm font-semibold text-[#1A1F2B] mb-2 text-center" data-id="element-876">
                      رمز التحقق (OTP)
                    </label>
                    <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" className="w-full bg-[#f8f9fb] border border-[#E2E8F0] rounded-xl py-3.5 px-4 text-center font-sans text-2xl tracking-[0.5em] text-[#0B1328] placeholder:text-[#B0BAC8] focus:outline-none focus:ring-2 focus:ring-[#2FA99C]/50 focus:border-[#2FA99C] transition-all" dir="ltr" autoFocus data-id="element-877" />
                    {error && <p className="mt-2 text-sm text-red-500 font-arabic-body text-center" data-id="element-878">
                        {error}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading || otp.length < 4} className="mt-2 w-full bg-[#2FA99C] hover:bg-[#268f84] text-white rounded-xl py-3.5 font-arabic-body font-bold text-lg flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_8px_20px_-6px_rgba(47,169,156,0.4)] hover:shadow-[0_12px_24px_-6px_rgba(47,169,156,0.5)] hover:-translate-y-0.5" data-id="element-879">
                    {isLoading ? <Loader2Icon className="w-5 h-5 animate-spin" data-id="element-880" /> : <span data-id="element-881">تأكيد الدخول</span>}
                  </button>

                  <button type="button" onClick={() => setStep('email')} className="text-sm text-[#6E7A8F] hover:text-[#2FA99C] font-arabic-body transition-colors" data-id="element-882">
                    تعديل البريد الإلكتروني
                  </button>
                </form>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>;
}